/**
 * /for-carriers — the carrier-audience landing. Carrier is a first-class
 * audience now (the standing HOLD was lifted by the owner 2026-10-06), but it is
 * NOT a self-serve seat: carrier firms are onboarded by platform staff (who set
 * `law_firms.client_side = "carrier"`), so the CTA is a walkthrough/contact, not
 * a signup. A carrier user then signs in with their normal role (attorney / CPA /
 * adjuster) — the firm's `client_side` flips the FRAMING, never the engine math.
 *
 * Court-exhibit system (rules not fills, DM Serif / DM Mono / Outfit, one orange
 * accent, square geometry). The value prop is SIDE-NEUTRALITY: the same forensic
 * engines that build the plaintiff's demand compute the carrier's defensible floor.
 */

import PublicShell from '../components/marketing/PublicShell';
import { SectionHeader, ExhibitCell, Provenance, PrimaryCTA, SectionLink } from '../components/home/sectionPrimitives';

const HAIR = { borderColor: 'var(--color-hairline)' } as const;
const DOUBLE_RULE = {
  borderTop: '1px solid var(--color-text-dim)',
  borderBottom: '3px double var(--color-text-dim)',
} as const;

const CAPABILITIES: Array<{ index: string; title: string; body: string }> = [
  {
    index: '01',
    title: 'Carrier position ledger',
    body: 'The defensible floor = supported demand (N) minus attestable reductions. Advocacy-tier (Tier-3) adjustments are quarantined to their own column — never folded into the number you take to the table.',
  },
  {
    index: '02',
    title: 'Counter-model',
    body: "Reverse-engineer the demand: drop in the other side's fixed/variable classification, rank every attack vector, and Daubert-score each one.",
  },
  {
    index: '03',
    title: 'Neutral settlement view',
    body: 'The midpoint between the demand and the floor — the number a mediator can actually price, grounded in the same engines both sides read.',
  },
  {
    index: '04',
    title: 'Court-ready reconciliation',
    body: "Exhibit C — “Reconciliation of Carrier Position” — generated into the same court bundle every figure already traces to.",
  },
];

/** Dense mono spec list for the side-neutral section (white/dim type — no new orange). */
const SIDE_NEUTRAL_SPEC: Array<[string, string]> = [
  ['Engine math', 'Identical both sides'],
  ['Framing', 'Reconciled to your position'],
  ['Reductions', 'Itemized + attestable'],
  ['Advocacy', 'Quarantined from the floor'],
];

/** The floor as a ledger: supported net − attestable reductions = defensible floor. */
function FloorExhibit() {
  return (
    <div className="w-full border bg-surface" style={HAIR}>
      <div
        className="flex items-center justify-between gap-4 border-b px-5 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-text-dim sm:px-6"
        style={HAIR}
      >
        <span>Exhibit · Carrier position</span>
        <span className="shrink-0">Illustrative</span>
      </div>
      <div className="px-5 py-5 sm:px-6">
        <div className="flex items-baseline justify-between gap-4 py-2">
          <span className="font-mono text-sm text-text-secondary">Supported demand (N)</span>
          <span className="font-mono text-sm tabular-nums text-text-primary">$155,309</span>
        </div>
        <div className="flex items-baseline justify-between gap-4 py-2">
          <span className="font-mono text-sm text-text-muted">Attestable reductions</span>
          <span className="font-mono text-sm tabular-nums text-text-muted">($29,513)</span>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-dim">
          Itemized · each traced to an attestation
        </p>
        <div className="mt-2 flex items-baseline justify-between gap-4 pt-3 pb-4" style={DOUBLE_RULE}>
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-dim">Defensible floor</span>
          <span className="font-heading text-4xl leading-none tracking-tight text-brand-text">$125,796</span>
        </div>
      </div>
      <p
        className="border-t px-5 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-text-dim sm:px-6"
        style={HAIR}
      >
        Advocacy adjustments quarantined — never in the floor
      </p>
    </div>
  );
}

export default function ForCarriersPage() {
  return (
    <PublicShell wide>
      {/* Hero — argument left, floor exhibit right */}
      <section className="mx-auto max-w-[1100px] px-5 pb-16 pt-10 sm:px-8 lg:pb-24 lg:pt-16">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-12">
          <div className="lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-text">For carriers</p>
            <h1 className="mt-5 font-heading text-4xl leading-[1.05] tracking-tight text-text-primary md:text-5xl lg:text-6xl">
              A defensible floor — not a lowball.
            </h1>
            <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-text-secondary">
              The same forensic engines that build the plaintiff’s demand compute the carrier’s position.
              Side-neutral math; every reduction attestable and traceable. The number that holds up in court —
              read from your side of the claim.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <PrimaryCTA to="/for-carriers/start">Become a client</PrimaryCTA>
              <SectionLink to="/sign-in">Sign in</SectionLink>
            </div>
          </div>
          <div className="mt-12 lg:col-span-6 lg:mt-0 lg:pl-4">
            <FloorExhibit />
          </div>
        </div>
      </section>

      {/* Side-neutral proof — argument left, dense spec register right */}
      <section className="border-t border-hairline">
        <div className="mx-auto max-w-[1100px] px-5 py-16 sm:px-8 lg:py-24">
          <div className="lg:grid lg:grid-cols-12 lg:gap-x-12">
            <div className="lg:col-span-7">
              <SectionHeader
                eyebrow="Side-neutral by design"
                title="Both sides read the same math."
                lede="The engines never change by side — what changes is the reconciliation, not the math. The carrier sees the defensible floor instead of the supported demand, with the reductions itemized, attestable, and quarantined from the figure that holds up. It isn’t an advocacy tool. It’s the same forensic record, reconciled to your position."
              />
            </div>
            <div className="mt-10 lg:col-span-4 lg:col-start-9 lg:mt-2">
              <dl className="border-t" style={HAIR}>
                {SIDE_NEUTRAL_SPEC.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-4 border-b py-3.5" style={HAIR}>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">{k}</dt>
                    <dd className="text-right font-mono text-[11px] uppercase tracking-[0.1em] text-text-secondary">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* What the carrier seat gets */}
      <section className="border-t border-hairline">
        <div className="mx-auto max-w-[1100px] px-5 py-16 sm:px-8 lg:py-24">
          <SectionHeader eyebrow="What you get" title="Engine-grounded, from the first number to the exhibit." />
          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2">
            {CAPABILITIES.map((c) => (
              <ExhibitCell key={c.index} index={c.index} title={c.title} body={c.body} />
            ))}
          </div>
          <Provenance
            note="Same engine outputs both sides read · every reduction traces to an attestation"
            action={<SectionLink to="/contact?audience=carrier">Talk to us →</SectionLink>}
          />
        </div>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-hairline">
        <div className="mx-auto max-w-[1100px] px-5 py-16 text-center sm:px-8 lg:py-20">
          <h2 className="mx-auto max-w-2xl font-heading text-3xl leading-tight tracking-tight text-text-primary md:text-4xl">
            Hold a number that holds up.
          </h2>
          <p className="mx-auto mt-5 max-w-lg font-body text-base leading-relaxed text-text-secondary">
            We onboard carriers directly. Become a client and we’ll stand up your side on a real matter.
          </p>
          <div className="mt-9 flex justify-center">
            <PrimaryCTA to="/for-carriers/start">Become a client</PrimaryCTA>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
